/** @flow */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import map from 'lodash/map'
import { Badge } from '@mui/material'
import { connect, useDispatch, useSelector } from 'react-redux'
import FontIcon from 'app/components/FontIcon/FontIcon.jsx'
import { showPanel } from 'app/components/OverPage/OverPage.jsx'
import api from 'app/core/api/api.js'
import history from 'app/main/routerHistory'
import type { User } from 'app/core/types'
import { Permission, permission } from 'app/containers/Permissions'
import { durationStr } from 'app/components/Duration/Duration.tsx'
import { IconButton } from 'app/components/Form/Button/IconButton.tsx'
import { groupBy } from 'lodash'
import { fetchTodayMyActivities } from 'app/store/reducers/reduxActivities.ts'
import { myTodayActivitiesWithRelationsSelector } from 'app/store/selectors/reduxActivities.ts'
import {
  fetchNotifications,
  addNotification,
  incrementUnreadNotifications,
  resetUnreadNotifications,
} from 'app/store/reducers/notifications.ts'
import NotificationsRightPanel, { maxNotifsToDisplay } from 'app/containers/Notifications/NotificationsRightPanel.tsx'
import { unreadNotificationsCountSelector, notificationsSelector } from 'app/store/selectors/notifications.ts'
import { WebsocketContext } from 'app/core/api/WebsocketProvider.tsx'
import classes from './Header.module.scss'

type Props = {|
  user: User,
  unreadNotifs?: number,
  pathname: string,
|}

const itemTypes = { TIMER: 'Timer', NOTIFICATIONS: 'Notifications' }

function IconsNav(props: Props): React$Node {
  const { user, pathname } = props

  const { isReady, ws }: { isReady: boolean, ws: any } = useContext(WebsocketContext)

  const dispatch = useDispatch()

  const myTodayActivities = useSelector(myTodayActivitiesWithRelationsSelector)
  const unreadNotifs = useSelector(unreadNotificationsCountSelector)
  const notifications = useSelector(notificationsSelector)

  const [clickedItem, setClickedItem] = useState(null)
  const [rightPanelIsOpen, setRightPanelIsOpen] = useState(false)

  const allActivitiesById = groupBy(myTodayActivities, 'id')
  const updatedActivitiesByGroup = map(allActivitiesById, (el) => {
    const currentTime = Date.now()
    return el.reduce((closest, date) => {
      return Math.abs(currentTime - (date?.updatedAt ?? 0)) < Math.abs(currentTime - (closest?.updatedAt ?? 0))
        ? date
        : closest
    })
  })

  const altDuration = myTodayActivities
    .filter((a) => a.taskInst?.taskTypeInst?.name === 'alt')
    .reduce((acc, a) => acc + a.duration, 0)
  const tskDuration = updatedActivitiesByGroup
    .filter((a) => !a.task || (a.taskInst?.taskTypeInst?.name && a.taskInst.taskTypeInst.name !== 'alt'))
    .reduce((acc, a) => acc + a.duration, 0)

  useEffect(() => {
    dispatch(
      fetchNotifications({
        id: user.asset,
        params: {
          queries: {
            page_size: maxNotifsToDisplay,
          },
        },
      }),
    )
  }, [user])

  useEffect(() => {
    if (isReady) {
      ws.addEventListener('message', (event) => {
        const wsData = JSON.parse(event.data)
        const { event: eventType, model, data } = wsData
        if (eventType === 'create' && model === 'Notification') {
          dispatch(incrementUnreadNotifications())
          dispatch(addNotification(data))
        }
      })
    }
  }, [isReady, user])

  const todayDate = useMemo(() => moment().format('YYYY-MM-DD'), [])

  useEffect(() => {
    if (permission(['my hours___read', 'my hours_my allotment hours__read'], 'or')) {
      dispatch(fetchTodayMyActivities({ date__range: `${todayDate},${todayDate}`, page: 1, page_size: 1000 }))
    }
  }, [])

  function onRequestClose() {
    setClickedItem(null)
    setRightPanelIsOpen(false)
  }

  async function onClickIconNotifications() {
    setRightPanelIsOpen(true)
    dispatch(resetUnreadNotifications())
    setClickedItem(itemTypes.NOTIFICATIONS)
    if (unreadNotifs === 0) {
      const res = await dispatch(
        fetchNotifications({
          id: user.asset,
          params: {
            queries: {
              page_size: maxNotifsToDisplay,
            },
          },
        }),
      )
      api.assets.update({
        id: user.asset,
        attributes: { ovm_lastSeenNotification: res.payload.results[0].id },
      })
    } else {
      api.assets.update({
        id: user.asset,
        attributes: { ovm_lastSeenNotification: notifications[0].id },
      })
    }
  }

  function renderRightPanelItem() {
    switch (clickedItem) {
      case itemTypes.NOTIFICATIONS:
        return <NotificationsRightPanel style={{ padding: 0 }} onCloseNotifications={onRequestClose} />
      default:
        return null
    }
  }

  function renderRightPanel() {
    if (!rightPanelIsOpen && !clickedItem) return null

    showPanel('notifications', renderRightPanelItem(), onRequestClose, {
      defaultWidth: 'auto',
    })
    return null
  }

  function isActive(uri) {
    return pathname.startsWith(uri)
  }

  return (
    <>
      <IconButton
        data-testid="nav-top-trombinoscope"
        to="/organization-chart"
        className={classes.button}
        style={{
          backgroundColor: isActive('/organization-chart') ? '#9ad535' : undefined,
          color: isActive('/organization-chart') ? '#ffffff' : undefined,
        }}
      >
        <FontIcon icon="fas-users" className={classes.buttonIcon} />
      </IconButton>
      <Permission actions={['my hours___read']}>
        <IconButton
          data-testid="nav-top-myhours"
          to="/my-hours"
          className={classes.button}
          style={{
            backgroundColor: isActive('/my-hours') ? '#9ad535' : undefined,
            color: isActive('/my-hours') ? '#ffffff' : undefined,
          }}
        >
          <div className="flex column center alignCenter fullWidth fullHeight">
            <FontIcon icon="fas-hourglass-half" className="fontSize16" />
            <div
              style={{
                whiteSpace: 'nowrap',
                fontWeight: 'bold',
                fontSize: '12px',
              }}
            >
              {durationStr(tskDuration, undefined, { days: true }, '[H]h[M]')}
            </div>
          </div>
        </IconButton>
      </Permission>
      <Permission actions={['my hours_my allotment hours__read']}>
        <IconButton
          data-testid="nav-top-myhours-allotment"
          to="/my-allotment-hours"
          className={classes.button}
          style={{
            backgroundColor: isActive('/my-allotment-hours') ? '#9ad535' : undefined,
            color: isActive('/my-allotment-hours') ? '#ffffff' : undefined,
          }}
        >
          <div className="flex column center alignCenter fullWidth fullHeight">
            <FontIcon icon="fas-clock" className="fontSize16" />
            <div
              style={{
                whiteSpace: 'nowrap',
                fontWeight: 'bold',
                fontSize: '12px',
              }}
            >
              {durationStr(altDuration, undefined, { days: true }, '[H]h[M]')}
            </div>
          </div>
        </IconButton>
      </Permission>
      <Permission actions={['notifications___read']}>
        <IconButton data-testid="nav-top-notifications" onClick={onClickIconNotifications} className={classes.button}>
          <>
            <FontIcon icon="fas-bell" className={classes.buttonIcon} />
            <Badge
              overlap="circular"
              badgeContent={unreadNotifs}
              max={99}
              color="primary"
              style={{ top: 10, right: 5, position: 'absolute' }}
            >
              <div />
            </Badge>
          </>
        </IconButton>
      </Permission>

      {renderRightPanel()}
    </>
  )
}

export default (connect<any, any, any, any, any, any>((state) => {
  return {
    pathname: history.location.pathname,
    user: state.user,
  }
})(IconsNav): Class<*> & ((props: Props) => React$Node))
